/**
 * Curbseal v1 unpack + WebCrypto ES256 verify.
 *
 * Mirrors backend/app/services/curbseal.py — the mint and this file must build
 * byte-identical signing preimages, so any change here needs the same change
 * there. WebCrypto verifies raw r||s directly, which is the form the seal
 * carries, so no signature re-encoding is needed.
 */

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const SERIAL_PREFIX = "MX";
const SERIAL_BITS = 50n;

const HEADER_LEN = 7;
const SIGNATURE_LEN = 64;
const SEAL_FORMAT_V1 = 0;
const TBS_CONTEXT = "curbseal/v1";

export const SEAL_LEN = HEADER_LEN + SIGNATURE_LEN;

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function b64urlEncode(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * The serial is 50 bits — two in the shared header byte, then six whole bytes.
 * BigInt rather than `>>`, which would silently truncate to 32 bits.
 */
function serialFromHeader(header) {
  let value = BigInt(header[0] & 0x03);
  for (let i = 1; i < HEADER_LEN; i++) value = (value << 8n) | BigInt(header[i]);

  let body = "";
  for (let shift = SERIAL_BITS - 5n; shift >= 0n; shift -= 5n) {
    body += CROCKFORD[Number((value >> shift) & 0x1fn)];
  }
  return SERIAL_PREFIX + body;
}

export function unpackSeal(sealBytes) {
  const bytes = sealBytes instanceof Uint8Array ? sealBytes : new Uint8Array(sealBytes);
  if (bytes.length !== SEAL_LEN) {
    throw new Error(`Not a Curb seal: expected ${SEAL_LEN} bytes, got ${bytes.length}`);
  }
  const fmt = bytes[0] >> 6;
  if (fmt !== SEAL_FORMAT_V1) {
    throw new Error(`Not a Curb seal: unsupported format version ${fmt}`);
  }
  const header = bytes.slice(0, HEADER_LEN);
  return {
    fmt,
    kidIndex: (bytes[0] >> 2) & 0x0f,
    serial: serialFromHeader(header),
    header,
    signature: bytes.slice(HEADER_LEN),
  };
}

/**
 * TBS = "curbseal/v1" || header || len(assetCode) || assetCode
 *
 * The context string supplies the domain separation that COSE's Sig_structure
 * used to. Binding assetCode is what stops a genuine outer matrix verifying
 * after being peeled onto a different nested pointer.
 */
export function buildSealToBeSigned(header, assetCode) {
  const encoder = new TextEncoder();
  const context = encoder.encode(TBS_CONTEXT);
  const code = encoder.encode(String(assetCode || "").trim().toUpperCase());
  if (!code.length) throw new Error("assetCode is required to verify a seal");

  const out = new Uint8Array(context.length + HEADER_LEN + 1 + code.length);
  out.set(context, 0);
  out.set(header, context.length);
  out[context.length + HEADER_LEN] = code.length;
  out.set(code, context.length + HEADER_LEN + 1);
  return out;
}

export async function importManufacturerKey({ public_spki_b64, public_jwk }) {
  if (public_spki_b64) {
    return crypto.subtle.importKey(
      "spki",
      b64ToBytes(public_spki_b64),
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"]
    );
  }
  if (public_jwk) {
    const jwk = { ...public_jwk };
    delete jwk.use;
    return crypto.subtle.importKey("jwk", jwk, { name: "ECDSA", namedCurve: "P-256" }, false, [
      "verify",
    ]);
  }
  throw new Error("No public key material");
}

export async function verifySeal(sealBytes, cryptoKey, assetCode) {
  const seal = unpackSeal(sealBytes);
  const ok = await crypto.subtle.verify(
    { name: "ECDSA", hash: "SHA-256" },
    cryptoKey,
    seal.signature,
    buildSealToBeSigned(seal.header, assetCode)
  );
  return { ok, seal, sealBytes };
}

export function sealToB64url(sealBytes) {
  return b64urlEncode(sealBytes instanceof Uint8Array ? sealBytes : new Uint8Array(sealBytes));
}
